import { FormikErrors, FormikProps } from 'formik';
import { MutableRefObject, RefObject, useImperativeHandle } from 'react';

import { noop } from './noop';

type refType<T> = ((instance: T | null) => void) | MutableRefObject<T | null> | null;

export interface SubPageHandle {
    values: unknown | null;
    validate: <T = unknown>() => Promise<FormikErrors<T>>;
    submit: () => void | Promise<void>;
    reset: () => void;
}

export type FormikExtendedSubmit<T> = (
    values: T,
    isDirty: boolean,
    isValid: boolean,
    errors: FormikErrors<T>
) => Promise<void> | void;

export const useFormikSubPage = <T>(
    ref: refType<SubPageHandle>,
    formikRef: RefObject<FormikProps<T>> | MutableRefObject<FormikProps<T> | undefined>,
    callback: FormikExtendedSubmit<T>
) => {
    return useImperativeHandle(ref, () => ({
        values: formikRef.current?.values,
        validate: async () => formikRef.current?.validateForm() ?? Promise.resolve({}),
        submit: async () => {
            try {
                // Taken before anything is awaited: leaving a tab calls this on
                // the way out, and by the time a validation comes back the mask
                // is gone and the ref empty. Reading the form afterwards dropped
                // every input made on the tab (#2261).
                const form = formikRef.current;

                if (!form) {
                    return;
                }

                const { values, dirty: isDirty } = form;

                // Formik validates asynchronously, so reading `errors` right
                // after handleSubmit() hands back the state from before this
                // submit — empty on a form nobody has typed into. The store
                // then held "valid, no errors" while the mask painted its
                // required fields red, and a save on a new record reported
                // success without writing anything (#2212).
                const errs = await form.validateForm(values);

                // Marks the fields touched, so the mask shows what is missing —
                // only while it is still on the screen.
                if (formikRef.current) {
                    formikRef.current.handleSubmit();
                }

                callback(values, isDirty, 0 === Object.keys(errs).length, errs);
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(
                    'Error while subpage formik submit.\nThis may occur if the submit call is not awaited correctly.'
                );
                throw e;
            }
        },
        reset: () => {
            if (formikRef.current) {
                formikRef.current.resetForm();
            }
        },
    }));
};

export const useSubPage = (ref: refType<SubPageHandle>, callback: () => Promise<void>) => {
    return useImperativeHandle(ref, () => ({
        values: null,
        validate: () => Promise.resolve({}),
        submit: callback,
        reset: noop,
    }));
};
